// แปลงไฟล์ที่ครูอัปโหลด (PDF จากเครื่องสแกน หรือรูป JPG/PNG) เป็นภาพความสว่างทีละหน้า
// ทำในเบราว์เซอร์ทั้งหมด ไม่ส่งภาพขึ้นเซิร์ฟเวอร์ — ไฟล์สแกนทั้งห้องใหญ่เกินเพดานอัปโหลด 1 MB
// ของ nginx และเป็นลายมือของนักเรียนที่ไม่จำเป็นต้องเก็บ
//
// isEvalSupported: false ทุกครั้งที่เปิดไฟล์ ห้ามเอาออก — กันช่องโหว่แบบ CVE-2024-4367 (ฟอนต์ใน PDF
// ที่ประดิษฐ์ขึ้นรันโค้ดได้) ไว้อีกชั้น แม้รุ่นที่ใช้อยู่แก้แล้วก็ตาม
// worker เป็น .mjs (โหลดเป็น module worker) — เซิร์ฟเวอร์ต้องส่ง .mjs เป็น JavaScript ไม่ใช่ octet-stream

const TARGET_DPI = 200
const MAX_SIDE = 2600        // ภาพมือถือ 4000px ย่อลงก่อน — ละเอียดกว่านี้ไม่ช่วยอ่านช่องฝน 4.8 มม.
const THUMB_WIDTH = 720

let pdfjsPromise = null
const loadPdfjs = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((lib) => {
      lib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()
      return lib
    })
  }
  return pdfjsPromise
}

const toGray = (ctx, width, height) => {
  const rgba = ctx.getImageData(0, 0, width, height).data
  const data = new Uint8ClampedArray(width * height)
  for (let i = 0, j = 0; j < data.length; i += 4, j++) {
    data[j] = (rgba[i] * 299 + rgba[i + 1] * 587 + rgba[i + 2] * 114) / 1000
  }
  return data
}

export const thumbnail = (canvas) => {
  const scale = Math.min(1, THUMB_WIDTH / canvas.width)
  const t = document.createElement('canvas')
  t.width = Math.round(canvas.width * scale)
  t.height = Math.round(canvas.height * scale)
  t.getContext('2d').drawImage(canvas, 0, 0, t.width, t.height)
  return { url: t.toDataURL('image/jpeg', 0.75), scale, width: t.width, height: t.height }
}

const fromCanvas = (canvas, label) => {
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  const img = { width: canvas.width, height: canvas.height, data: toGray(ctx, canvas.width, canvas.height) }
  return { label, img, thumb: thumbnail(canvas) }
}

const whiteCanvas = (w, h) => {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, w, h)
  return { canvas, ctx }
}

async function* pdfPages(file) {
  const pdfjs = await loadPdfjs()
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer(), isEvalSupported: false }).promise
  try {
    for (let n = 1; n <= doc.numPages; n++) {
      const page = await doc.getPage(n)
      const base = page.getViewport({ scale: 1 })
      const scale = Math.min(TARGET_DPI / 72, MAX_SIDE / Math.max(base.width, base.height))
      const viewport = page.getViewport({ scale })
      const { canvas, ctx } = whiteCanvas(Math.round(viewport.width), Math.round(viewport.height))
      await page.render({ canvasContext: ctx, viewport }).promise
      page.cleanup()
      yield { ...fromCanvas(canvas, `${file.name} หน้า ${n}`), total: doc.numPages }
    }
  } finally {
    doc.destroy()
  }
}

async function* imagePage(file) {
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height))
  const { canvas, ctx } = whiteCanvas(Math.round(bitmap.width * scale), Math.round(bitmap.height * scale))
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close?.()
  yield { ...fromCanvas(canvas, file.name), total: 1 }
}

export const isSupported = (file) =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name) || /^image\/(jpeg|png|webp)$/.test(file.type)

// ไล่ทีละหน้า ทีละไฟล์ — ถือภาพเต็มไว้ทีละหน้าเดียว ที่เหลือเก็บแค่ภาพย่อ
export async function* loadPages(files) {
  for (const file of files) {
    if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) yield* pdfPages(file)
    else yield* imagePage(file)
  }
}
